import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type {
  RescueConversation,
  RespondRescueConversationRequest,
} from "@io-spoke/contracts/rescueConversation";
import {
  getAccessToken,
  getActiveOrgId,
  getStoredUser,
  getWorkspaceGeneration,
} from "../lib/authStorage";
import {
  createRescueConversation,
  getRescueConversation,
  RescueConversationConflict,
  respondRescueConversation,
} from "../lib/rescueConversation";
import { useAsyncAction } from "./useAsyncAction";

type Operation =
  | "load"
  | "restart"
  | RespondRescueConversationRequest["answer"];

export function getRescueConversationIdentity() {
  const token = getAccessToken();
  let principal = getStoredUser()?.email ?? "";
  if (token) {
    try {
      const encoded = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
      const claims = JSON.parse(atob(encoded));
      if (typeof claims.sub === "string") principal = claims.sub;
    } catch {
      // Opaque access tokens use the canonical stored account identity.
    }
  }
  return JSON.stringify([
    getActiveOrgId(),
    getWorkspaceGeneration(),
    principal,
    Boolean(token),
  ]);
}

/** Mounted only after an explicit investigation request; never starts on case load. */
export function useRescueConversation(caseId: string) {
  const action = useAsyncAction();
  const [conversation, setConversation] = useState<RescueConversation | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [operation, setOperation] = useState<Operation | null>(null);
  const [loaded, setLoaded] = useState(false);
  const context = useRef({
    caseId,
    identity: getRescueConversationIdentity(),
    version: 0,
  });
  const mounted = useRef(false);
  const foreground = useRef(false);

  useLayoutEffect(() => {
    mounted.current = true;
    if (
      context.current.caseId !== caseId ||
      context.current.identity !== getRescueConversationIdentity()
    ) {
      context.current = {
        caseId,
        identity: getRescueConversationIdentity(),
        version: context.current.version + 1,
      };
      setConversation(null);
      setLoaded(false);
      setError(null);
    }
    return () => {
      mounted.current = false;
    };
  }, [caseId]);

  const run = (kind: Operation) =>
    action.run(async () => {
      foreground.current = true;
      context.current.version += 1;
      const captured = { ...context.current };
      const identity = getRescueConversationIdentity();
      const current = () =>
        mounted.current &&
        context.current.version === captured.version &&
        context.current.caseId === caseId &&
        getRescueConversationIdentity() === identity;
      setOperation(kind);
      setError(null);
      const apply = (value: RescueConversation | null) => {
        if (!current()) return;
        if (value && value.rescueCaseId !== caseId) {
          throw new Error("The investigation belongs to a different case.");
        }
        setConversation(value);
        setLoaded(true);
      };
      try {
        if (kind === "load") {
          let result = await getRescueConversation(caseId);
          if (!current()) return;
          if (!result) result = await createRescueConversation(caseId);
          apply(result);
        } else if (kind === "restart") {
          apply(await createRescueConversation(caseId));
        } else if (conversation) {
          apply(
            await respondRescueConversation(caseId, conversation.id, {
              answer: kind,
            }),
          );
        }
      } catch (cause) {
        if (!current()) return;
        if (cause instanceof RescueConversationConflict) {
          try {
            apply(await getRescueConversation(caseId));
          } catch {
            if (current()) setLoaded(false);
          }
        }
        if (current()) {
          setError(
            cause instanceof Error
              ? cause.message
              : "Investigation unavailable. Try again.",
          );
        }
      } finally {
        foreground.current = false;
        if (current()) setOperation(null);
      }
    });

  const refresh = async () => {
    if (foreground.current || !mounted.current) return;
    const captured = { ...context.current };
    const identity = getRescueConversationIdentity();
    const result = await getRescueConversation(caseId);
    if (
      !mounted.current ||
      foreground.current ||
      context.current.version !== captured.version ||
      getRescueConversationIdentity() !== identity
    )
      return;
    if (result && result.rescueCaseId !== caseId)
      throw new Error("The investigation belongs to a different case.");
    setConversation(result);
    setLoaded(true);
  };

  useEffect(() => {
    const changed = () => {
      if (context.current.identity === getRescueConversationIdentity()) return;
      context.current.version += 1;
      context.current.identity = getRescueConversationIdentity();
      setConversation(null);
      setLoaded(false);
      setOperation(null);
      setError("Your account or workspace changed. Reload the investigation.");
    };
    window.addEventListener("auth:changed", changed);
    window.addEventListener("storage", changed);
    return () => {
      window.removeEventListener("auth:changed", changed);
      window.removeEventListener("storage", changed);
    };
  }, []);

  useEffect(() => {
    void run("load");
  }, [caseId]);
  return {
    conversation,
    error,
    operation,
    loaded,
    pending: action.pending,
    run,
    refresh,
  };
}
